import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Crown, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { BenchmarkResults } from './BenchmarkResults';
import { trackEvent } from '../lib/analytics';
import { api } from '../lib/api';

interface AIModel {
  id: string;
  name: string;
  company: string;
  aeo: number;
  aiReadiness: number;
}

const AI_MODELS: AIModel[] = [
  { id: 'chatgpt', name: 'ChatGPT', company: 'OpenAI', aeo: 85, aiReadiness: 92 },
  { id: 'perplexity', name: 'Perplexity AI', company: 'Perplexity AI', aeo: 78, aiReadiness: 88 },
  { id: 'claude', name: 'Claude', company: 'Anthropic', aeo: 82, aiReadiness: 90 },
  { id: 'gemini', name: 'Gemini', company: 'Google', aeo: 80, aiReadiness: 87 },
];

export function AIModelBenchmark() {
  const { profile } = useAuth();
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [userScores, setUserScores] = useState({ aeo: 0, aiReadiness: 0 });
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string>('');
  const [websiteUrl, setWebsiteUrl] = useState('');

  const tier = profile?.subscriptionTier || 'free';
  const allowedTiers = ['professional', 'business', 'enterprise'];
  const hasAccess = allowedTiers.includes(tier);

  const handleModelToggle = (modelId: string) => {
    if (!hasAccess || analyzing) return;

    setSelectedModels(prev =>
      prev.includes(modelId)
        ? prev.filter(id => id !== modelId)
        : [...prev, modelId]
    );
  };

  const handleSelectAll = () => {
    if (!hasAccess || analyzing) return;
    setSelectedModels(AI_MODELS.map(m => m.id));
  };

  const handleClearAll = () => {
    if (!hasAccess || analyzing) return;
    setSelectedModels([]);
  };

  const normalizeUrl = (raw: string): string => {
    const trimmed = raw.trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const handleAnalyze = async () => {
    if (!hasAccess || selectedModels.length === 0 || !websiteUrl.trim()) return;

    const normalizedUrl = normalizeUrl(websiteUrl);

    setAnalyzing(true);
    setError('');
    trackEvent('benchmark_started', 'benchmark', selectedModels.join(','));

    try {
      // Create new analysis
      const newAnalysis = await api.createAnalysis(normalizedUrl);

      // Trigger the analysis
      await api.analyzeWebsite(normalizedUrl, newAnalysis.id);

      // Poll for completion
      await api.pollAnalysisUntilComplete(newAnalysis.id, (updatedAnalysis) => {
        if (updatedAnalysis.status === 'completed') {
          setUserScores({
            aeo: updatedAnalysis.visibilityScore || 0,
            aiReadiness: updatedAnalysis.overallScore || 0,
          });
          setAnalyzing(false);
          setShowResults(true);
          trackEvent('benchmark_completed', 'benchmark', selectedModels.join(','));
        } else if (updatedAnalysis.status === 'failed') {
          setError('Analysis failed. Please try a different URL.');
          setAnalyzing(false);
        }
      });
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err instanceof Error ? err.message : 'Failed to analyze website. Please try again.');
      setAnalyzing(false);
    }
  };

  const getTierLabel = () => {
    const tierLabels: { [key: string]: string } = {
      'free': 'Free',
      'starter': 'Starter',
      'professional': 'Professional',
      'business': 'Business',
      'enterprise': 'Enterprise',
    };
    return tierLabels[tier] || 'Free';
  };

  if (showResults) {
    return (
      <BenchmarkResults
        userScores={userScores}
        selectedModels={AI_MODELS.filter(m => selectedModels.includes(m.id))}
        onBack={() => setShowResults(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="bg-white rounded-lg border border-gray-200 p-8 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                AI Model Benchmark Comparison
              </h1>
              <p className="text-gray-600">
                Compare your AEO and AI Readiness scores against industry-leading AI models
              </p>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-50 rounded-lg border border-yellow-200">
              <Crown className="w-4 h-4 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-700">Premium Feature</span>
            </div>
          </div>

          {!hasAccess && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900 mb-2">
                    Upgrade Required
                  </p>
                  <p className="text-sm text-blue-700 mb-3">
                    AI Model Benchmark Comparison is available for Professional, Business, and Enterprise plans.
                    You are currently on the {getTierLabel()} plan.
                  </p>
                  <button
                    onClick={() => {
                      window.location.href = '/?pricing=true';
                    }}
                    className="bg-blue-600 text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Upgrade Now
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* URL Input */}
        <div className="bg-white rounded-lg border border-gray-200 p-8 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Enter Website URL</h2>
          <input
            type="text"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="Enter your website URL (e.g. yourdomain.com)"
            disabled={!hasAccess || analyzing}
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
              hasAccess && !analyzing
                ? 'border-gray-300 bg-white'
                : 'border-gray-200 bg-gray-50 cursor-not-allowed'
            }`}
          />
        </div>

        {/* Model Selection */}
        <div className="bg-white rounded-lg border border-gray-200 p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Select AI Models</h2>
            <div className="flex gap-3">
              <button
                onClick={handleSelectAll}
                disabled={!hasAccess || analyzing}
                className={`text-sm font-medium py-2 px-4 rounded-lg transition-colors ${
                  hasAccess && !analyzing
                    ? 'text-gray-700 hover:bg-gray-100'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
              >
                Select All
              </button>
              <button
                onClick={handleClearAll}
                disabled={!hasAccess || analyzing}
                className={`text-sm font-medium py-2 px-4 rounded-lg transition-colors ${
                  hasAccess && !analyzing
                    ? 'text-gray-700 hover:bg-gray-100'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
              >
                Clear All
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {AI_MODELS.map((model) => (
              <label
                key={model.id}
                className={`relative flex items-start p-4 border rounded-lg cursor-pointer transition-all ${
                  hasAccess && !analyzing
                    ? selectedModels.includes(model.id)
                      ? 'border-black bg-gray-50'
                      : 'border-gray-200 hover:border-gray-300'
                    : 'border-gray-200 opacity-50 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    checked={selectedModels.includes(model.id)}
                    onChange={() => handleModelToggle(model.id)}
                    disabled={!hasAccess || analyzing}
                    className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
                  />
                </div>
                <div className="ml-3 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">{model.name}</span>
                    {selectedModels.includes(model.id) && hasAccess && (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{model.company}</p>
                  <div className="mt-2 text-xs text-gray-500">
                    AEO: {model.aeo} | AI Readiness: {model.aiReadiness}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-white rounded-lg border border-red-200 p-8 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Analyzing State */}
        {analyzing && (
          <div className="bg-white rounded-lg border border-gray-200 p-8 mb-6">
            <div className="flex flex-col items-center justify-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-black" />
              <div className="text-center">
                <p className="text-lg font-medium text-gray-900">Analyzing website...</p>
                <p className="text-sm text-gray-500 mt-1">
                  This may take up to 30 seconds
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Analyze Button */}
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <button
            onClick={handleAnalyze}
            disabled={!hasAccess || selectedModels.length === 0 || !websiteUrl.trim() || analyzing}
            className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${
              hasAccess && selectedModels.length > 0 && websiteUrl.trim() && !analyzing
                ? 'bg-black text-white hover:bg-gray-800'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {!hasAccess
              ? 'Upgrade to Access Benchmark Comparison'
              : analyzing
              ? 'Analyzing...'
              : !websiteUrl.trim()
              ? 'Enter a website URL to analyze'
              : selectedModels.length === 0
              ? 'Select at least one model to compare'
              : `Analyze & Compare (${selectedModels.length} model${selectedModels.length > 1 ? 's' : ''} selected)`}
          </button>

          {hasAccess && selectedModels.length > 0 && websiteUrl.trim() && !analyzing && (
            <p className="text-sm text-gray-500 text-center mt-3">
              We'll analyze {websiteUrl} and compare it against {selectedModels.length} AI model
              {selectedModels.length > 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
